import { computed, type Ref } from "vue";
import {
  isExclusiveVariant,
  exclusiveBadgeText as exclusiveTextFromVariant,
  readVariantQty,
  isItemInStock,
} from "~/composables/product/variantDisplay";

/* ====================================================================
 *  useCardVariantView — derived display-стан картки товару:
 *  лінк (deep-link на лот), підпис варіанта, ціна, бейджі,
 *  списки опцій і готовність вибору (canSubmit).
 *  Тільки обчислення — без стору, без I/O, без DOM.
 * ==================================================================== */

type AnyRecord = Record<string, unknown>;

interface Opt {
  label: string;
  value: string;
}

function normalizeOpt(opt: unknown): Opt {
  return opt && typeof opt === "object" && "value" in opt
    ? (opt as Opt)
    : { label: String(opt), value: String(opt) };
}

export function useCardVariantView(
  product: () => AnyRecord,
  variant: () => AnyRecord | null | undefined,
  selectedColor: Ref<string>,
  selectedSize: Ref<string>,
) {
  const link = computed(() => {
    const p = product();
    const base = `/product/${p.slug || p.id}`;
    const v = variant();

    // Deep-link на конкретний лот, коли він відомий: сторінка товару
    // розуміє `vid` (variant id) і обере саме цей лот.
    const vid = v?.id ? String(v.id) : "";

    const c = (v?.color as AnyRecord)?.value ?? v?.color ?? "";
    const s = v?.size ?? "";

    const qs: string[] = [];
    if (c) qs.push(`color=${encodeURIComponent(String(c))}`);
    if (s) qs.push(`size=${encodeURIComponent(String(s))}`);
    if (vid) qs.push(`vid=${encodeURIComponent(vid)}`);

    return qs.length ? `${base}?${qs.join("&")}` : base;
  });

  const priceToShow = computed(() =>
    Number((variant()?.price ?? product().price ?? 0) as number),
  );

  const isExclusive = computed(() => isExclusiveVariant(variant()));

  const exclusiveBadgeText = computed(() =>
    exclusiveTextFromVariant(variant()),
  );

  const isInStock = computed(() => isItemInStock(product(), variant()));

  const isOutOfStock = computed(() => !isInStock.value);

  const isLonelyBeauty = computed(() => {
    const q = readVariantQty(variant());
    // Показуємо тільки коли кількість впевнено читається з варіанта
    return isInStock.value === true && q === 1;
  });

  const sizesList = computed(() =>
    ((product().sizes || product().sizeOptions || []) as unknown[]).map(
      normalizeOpt,
    ),
  );
  const colorsList = computed(() =>
    ((product().colors || product().colorOptions || []) as unknown[]).map(
      normalizeOpt,
    ),
  );

  const variantLabel = computed(() => {
    const v = variant();

    // Колір буває {label,value} АБО голим рядком-slug'ом.
    const colorValue = String(
      (v?.color as AnyRecord)?.value ?? v?.color ?? "",
    ).trim();
    const colorLabelFromVariant = String(
      (v?.color as AnyRecord)?.label ?? "",
    ).trim();

    // Пріоритет — явний label з варіанта; інакше мапимо value -> label
    // через опції кольорів товару.
    let colorLabel = colorLabelFromVariant;
    if (!colorLabel && colorValue && Array.isArray(colorsList.value)) {
      const match = colorsList.value.find(
        (c) => String(c.value) === colorValue,
      );
      if (match?.label) colorLabel = String(match.label);
    }
    if (!colorLabel) colorLabel = colorValue;

    const sizeLabel = String(v?.size ?? "").trim();

    if (colorLabel && sizeLabel) return `${colorLabel}, ${sizeLabel}`;
    if (colorLabel) return String(colorLabel);
    if (sizeLabel) return String(sizeLabel);
    return "";
  });

  const needSize = computed(() => sizesList.value.length > 0);
  const needColor = computed(() => colorsList.value.length > 0);
  const canSubmit = computed(
    () =>
      (!needSize.value || !!selectedSize.value) &&
      (!needColor.value || !!selectedColor.value),
  );

  return {
    link,
    priceToShow,
    isExclusive,
    exclusiveBadgeText,
    isInStock,
    isOutOfStock,
    isLonelyBeauty,
    sizesList,
    colorsList,
    variantLabel,
    needSize,
    needColor,
    canSubmit,
  };
}
